import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table, Typography, message
} from 'antd';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import { rollbackImport } from './importHelpers';

const { Title } = Typography;

export default function MassImportResults({
  success,
  failed,
  headers,
  importTypeConfig,
  importType,
  onRollbackComplete,
  onDone,
}) {
  const [rollingBack, setRollingBack] = useState(false);

  const successColumns = useMemo(() => {
    const base = (headers || []).map((header) => ({
      title: header.title,
      dataIndex: header.key,
      key: header.key,
      ellipsis: true,
    }));
    return [
      {
        title: 'Row',
        dataIndex: 'rowNumber',
        key: 'rowNumber',
        width: 72,
      },
      ...base,
    ];
  }, [headers]);

  const successData = useMemo(
    () => (success || []).map((s, idx) => {
      const row = s.displayRow || s.item || {};
      return {
        key: idx,
        rowNumber: s.rowNumber,
        ...row,
      };
    }),
    [success],
  );

  const failedColumns = useMemo(() => {
    const headerCols = (headers || []).map((header) => ({
      title: header.title,
      dataIndex: header.key,
      key: header.key,
      ellipsis: true,
    }));
    return [
      {
        title: 'Row',
        dataIndex: 'rowNumber',
        key: 'rowNumber',
        width: 72,
      },
      {
        title: 'Error',
        dataIndex: 'error',
        key: 'error',
        ellipsis: true,
        render: (error) => <span style={{ color: 'red', fontWeight: 'bold' }}>{error}</span>,
      },
      ...headerCols,
    ];
  }, [headers]);

  const failedData = useMemo(
    () => (failed || []).map((f, idx) => {
      const row = f.originalRow || {};
      return {
        key: idx,
        rowNumber: f.rowNumber,
        error: f.error,
        ...row,
      };
    }),
    [failed],
  );

  const handleRollback = async () => {
    if (!success?.length || !importType) return;
    setRollingBack(true);
    try {
      const result = await rollbackImport(importType, success);
      if (result.failed.length > 0) {
        message.warning(
          `Rolled back ${result.rolledBack} items. ${result.failed.length} could not be removed: ${result.failed.map((f) => f.error).join('; ')}`,
        );
      } else {
        message.success(`Successfully removed all ${result.rolledBack} imported items.`);
      }
      onRollbackComplete?.(result);
    } catch (err) {
      message.error(`Rollback failed: ${err.message}`);
    } finally {
      setRollingBack(false);
    }
  };

  const label = importTypeConfig?.label?.toLowerCase() || 'items';
  const successText = importTypeConfig?.resultsSuccessText || 'imported';
  const supportRollback = importTypeConfig?.supportRollback !== false;
  const hasSuccess = success?.length > 0;
  const hasFailed = failed?.length > 0;

  return (
    <div>
      <Title level={4}>Import results</Title>
      <p style={{ marginBottom: 16, color: '#666' }}>
        {hasSuccess && (
          <span>
            {success.length} {label} {successText} successfully.
          </span>
        )}
        {hasSuccess && hasFailed && ' '}
        {hasFailed && (
          <span>
            {failed.length} {label} failed.
          </span>
        )}
      </p>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 24,
        }}
      >
        {hasSuccess && supportRollback && (
          <OnTraccrButton
            title="Rollback import"
            onClick={handleRollback}
            loading={rollingBack}
            type="reject"
          />
        )}
        <OnTraccrButton
          title="Done"
          onClick={onDone}
          type="primary"
        />
      </div>

      {hasSuccess && (
        <>
          <Title level={5}>Successful imports</Title>
          <Table
            columns={successColumns}
            dataSource={successData}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
            size="small"
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      {hasFailed && (
        <>
          <Title level={5}>Failed imports</Title>
          <Table
            columns={failedColumns}
            dataSource={failedData}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
            size="small"
            style={{ marginBottom: 24 }}
          />
        </>
      )}
    </div>
  );
}

MassImportResults.propTypes = {
  success: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      item: PropTypes.shape({}),
      response: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
      displayRow: PropTypes.shape({}),
      rowNumber: PropTypes.number,
    }),
  ),
  failed: PropTypes.arrayOf(
    PropTypes.shape({
      rowNumber: PropTypes.number,
      error: PropTypes.string,
      originalRow: PropTypes.shape({}),
    }),
  ),
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      title: PropTypes.string,
      required: PropTypes.bool,
    }),
  ),
  importTypeConfig: PropTypes.shape({
    label: PropTypes.string,
    route: PropTypes.string,
    supportRollback: PropTypes.bool,
    resultsSuccessText: PropTypes.string,
  }),
  importType: PropTypes.string,
  onRollbackComplete: PropTypes.func,
  onDone: PropTypes.func.isRequired,
};

MassImportResults.defaultProps = {
  success: [],
  failed: [],
  headers: [],
  importTypeConfig: null,
  importType: null,
  onRollbackComplete: null,
};
